import { Controller } from "@hotwired/stimulus"

// Appends a spinner on click and removes it when the Turbo request responds
export default class extends Controller {
  start() {
    if (this.spinnerEl) return

    const spinner = document.createElement("span")
    spinner.className = "ml-2 inline-flex h-4 w-4 items-center justify-center text-current"
    spinner.setAttribute("role", "status")
    spinner.setAttribute("aria-label", "Loading")
    spinner.innerHTML = `
      <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-4a6 6 0 0 0-6-6V2z"></path>
      </svg>
    `

    this.spinnerEl = spinner
    this.element.setAttribute("aria-busy", "true")
    this.element.appendChild(spinner)

    // Prefer scoping to the specific target (frame or form) to avoid conflicts
    const frameId = this.element?.dataset?.turboFrame
    const frameEl = frameId ? document.getElementById(frameId) : null
    const formEl = this.element.closest("form")

    const clear = () => this.#removeSpinner()

    if (frameEl) {
      frameEl.addEventListener("turbo:before-fetch-response", clear, { once: true })
      return
    }

    if (formEl) {
      formEl.addEventListener("turbo:before-fetch-response", clear, { once: true })
      return
    }

    // Fallback for plain link navigations (full-page or non-frame)
    const linkHref = this.element.href
    const onBeforeFetchResponse = (e) => {
      try {
        const url = e.detail?.fetchResponse?.response?.url
        if (url && linkHref && this.#urlsMatch(linkHref, url)) clear()
      } catch (_) {
        // If anything is off, just clear to avoid a stuck spinner
        clear()
      }
    }
    document.addEventListener("turbo:before-fetch-response", onBeforeFetchResponse, { once: true })
  }

  #removeSpinner() {
    if (this.spinnerEl) {
      this.spinnerEl.remove()
      this.spinnerEl = null
    }

    this.element.removeAttribute("aria-busy")
  }

  #urlsMatch(a, b) {
    try {
      const ua = new URL(a, window.location.href)
      const ub = new URL(b, window.location.href)
      // Ignore trailing slashes and hash for comparison
      const norm = (u) => `${u.origin}${u.pathname.replace(/\/$/, "")}${u.search}`
      return norm(ua) === norm(ub)
    } catch (_) {
      return a === b
    }
  }
}
